/**
 * Fetch utilities with cache-busting support
 * 
 * Adds ?ts=YYYYMMDDHHmmss to URLs to prevent browser caching of JSON files.
 * This is especially important for OEM configuration and navigation files
 * that may be updated on the server but cached by the browser.
 */

/**
 * Generate a timestamp string in YYYYMMDDHHmmss format
 */
export function getTimestamp(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}${hours}${minutes}${seconds}`;
}

/**
 * Add cache-busting timestamp to a URL
 * @param url - The URL to add timestamp to
 * @returns URL with ?ts=... or &ts=... appended
 */
export function addCacheBuster(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}ts=${getTimestamp()}`;
}

/**
 * Fetch with automatic cache-busting for JSON files
 * Use this instead of fetch() when loading JSON that may change between deployments.
 * 
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @returns Fetch response
 */
export async function fetchWithCacheBust(url: string, options?: RequestInit): Promise<Response> {
  return fetch(addCacheBuster(url), options);
}

/**
 * Fetch JSON with automatic cache-busting and parsing
 * 
 * @param url - The URL to fetch
 * @returns Parsed JSON data
 * @throws Error if fetch fails or response is not OK
 */
export async function fetchJSON<T = unknown>(url: string): Promise<T> {
  const response = await fetchWithCacheBust(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
  }
  return response.json();
}
